"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { BlockRenderer } from "@/components/block-renderer";
import { Img } from "@/components/image";
import { buttonVariants } from "@/components/ui/button";
import type { DietHubHeroSingleType } from "@/features/hubs/types/diet-hub.type";
import { formatCateringName } from "@/lib/helpers/format-brand-name";
import { cn } from "@/lib/utils";

const AUTOPLAY_MS = 4200;

function useCarousel(length: number, delay: number, isPaused: boolean) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (length <= 1 || isPaused) {
      return;
    }

    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % length);
    }, delay);

    return () => clearInterval(id);
  }, [length, delay, isPaused]);

  return { activeIndex };
}

type Props = {
  title: string;
  label: string;
  data: DietHubHeroSingleType | undefined;
  strapiUrl: string;
};

export default function DietHubHeroSingle({
  title,
  label,
  data,
  strapiUrl,
}: Props) {
  const [isPaused, setIsPaused] = useState(false);

  const getImageUrl = (path: string) => {
    if (path.startsWith("http")) {
      return path;
    }
    return `${strapiUrl}${path}`;
  };
  const firstSlide = data?.heroCarousel[0];

  const { activeIndex } = useCarousel(
    data?.heroCarousel.length ?? 0,
    AUTOPLAY_MS,
    isPaused,
  );

  if (!firstSlide) {
    return null;
  }

  const activeSlide = data?.heroCarousel.at(activeIndex) ?? firstSlide;
  const activeSlideImage = activeSlide.image;
  const activeSlideImageUrl = activeSlideImage?.url
    ? getImageUrl(activeSlideImage.url)
    : null;
  const activeSlideLogo = activeSlide.cateringLogo;
  const activeSlideLogoUrl = activeSlideLogo?.url
    ? getImageUrl(activeSlideLogo.url)
    : null;

  if (!data) {
    return null;
  }

  return (
    <section className="flex flex-col justify-start gap-20 max-md:pt-10 lg:flex-row lg:items-center xl:gap-30">
      <div className="max-w-150">
        <div className="md:space-y-2">
          <p className="font-medium text-brand-violet uppercase">{label}</p>
          <h1 className="font-medium text-4xl text-brand-green1-300 leading-14 max-md:pb-1 md:text-5xl">
            {title}
          </h1>
          <BlockRenderer content={data.description} />
        </div>
        <div className="mt-6 flex flex-col items-center gap-4 md:flex-row">
          {data.callback && (
            <Link
              className={cn(
                buttonVariants({ variant: "secondary", size: "wide" }),
                "max-md:w-full",
              )}
              href={data.callback.href}
            >
              {data.callback.label}
            </Link>
          )}
          {data.secondCallback && (
            <Link
              className={cn(buttonVariants({ variant: "link" }))}
              href={data.secondCallback.href}
            >
              {data.secondCallback.label}
            </Link>
          )}
        </div>
      </div>

      <div className="relative h-115 w-full shrink-0 overflow-hidden rounded-xl bg-brand-white lg:w-lg">
        <button
          type="button"
          className="absolute top-4 right-4 z-10 flex size-14 items-center justify-center rounded-xl border bg-brand-white"
          onClick={() => setIsPaused((currentState) => !currentState)}
        >
          {isPaused ? (
            <Play className="size-6" />
          ) : (
            <Pause className="size-6" />
          )}
        </button>
        <AnimatePresence initial={false} mode="sync">
          <motion.div
            key={`${activeIndex}-${activeSlide.cateringName}-image`}
            variants={{
              enter: {
                y: "-100%",
              },
              center: {
                y: "0%",
              },
              exit: {
                y: "100%",
              },
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.72, ease: [0.7, 0, 0.3, 1] }}
            className="absolute inset-0"
          >
            <Img
              src={activeSlideImageUrl || ""}
              alt={
                activeSlideImage?.name ??
                formatCateringName(activeSlide.cateringName)
              }
              width={activeSlideImage?.width}
              height={activeSlideImage?.height}
              className="h-full w-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        <div className="absolute inset-x-4 bottom-4 z-10 h-20 overflow-hidden rounded-2xl bg-brand-white sm:inset-x-5 sm:bottom-5">
          <AnimatePresence initial={false} mode="sync">
            <motion.div
              key={`${activeIndex}-${activeSlide.cateringName}-card`}
              variants={{
                enter: {
                  y: "-100%",
                  opacity: 0,
                },
                center: {
                  y: "0%",
                  opacity: 1,
                },
                exit: {
                  y: "100%",
                  opacity: 0,
                },
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.48,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="absolute inset-0 flex items-center justify-between gap-4 px-2"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-grey1 p-2">
                  <Img
                    src={activeSlideLogoUrl || ""}
                    alt={formatCateringName(activeSlide.cateringName)}
                    width={activeSlideLogo?.width}
                    height={activeSlideLogo?.height}
                    className="w-full object-contain"
                  />
                </div>
                <div className="max-md:space-y-1">
                  <p className="font-medium text-base text-brand-green1-300 md:text-xl">
                    {formatCateringName(activeSlide.cateringName)}
                  </p>
                  <p className="pr-4 text-brand-green1-300 text-sm md:hidden">
                    {activeSlide.cateringPrice}
                  </p>
                </div>
              </div>

              <p className="hidden pr-4 text-brand-green1-300 text-xl md:block">
                {activeSlide.cateringPrice}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
